/**
 * Corbeille des entrees (« Corbeille » de Bitwarden, « Recently Deleted » de
 * 1Password, corbeille de Proton Pass) : « Supprimer » ne detruit plus rien
 * d'un geste. L'entree part a la corbeille, hors de toutes les vues (listes,
 * favoris, dossiers, archives, recherche, Sante du coffre, export) ; elle se
 * restaure telle quelle pendant TRASH_RETENTION_DAYS jours, puis la purge la
 * transforme en suppression definitive (tombstone, `deleteEntry`). « Supprimer
 * definitivement » et « Vider la corbeille » font la meme chose tout de suite.
 *
 * La date de mise a la corbeille vit DANS les secrets chiffres
 * (`secrets.trashedAt`, millisecondes), comme `archived` et `reprompt` : le
 * serveur ne sait pas quelles entrees sont a la corbeille, il ne voit qu'une
 * revision qui avance. Module pur, zero E/S.
 */
import type { EntrySecrets } from '@klavox/crypto-core';

/** Duree de conservation, alignee sur Bitwarden et Proton Pass (30 jours). */
export const TRASH_RETENTION_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Quelles entrees une liste montre : hors corbeille (defaut), corbeille seule, ou toutes. */
export type TrashFilter = 'exclude' | 'only' | 'include';

/** Date de mise a la corbeille, ou `null`. Seul un horodatage positif fini compte (valeur mal formee = pas a la corbeille). */
export function trashedAt(secrets: EntrySecrets): number | null {
  const value = secrets['trashedAt'];
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
}

export function isTrashed(secrets: EntrySecrets): boolean {
  return trashedAt(secrets) !== null;
}

/** Secrets mis a la corbeille a `at` (ou restaures si `null`) ; rien d'autre ne change. */
export function withTrashed(secrets: EntrySecrets, at: number | null): EntrySecrets {
  const { trashedAt: _trashedAt, ...rest } = secrets;
  return at === null ? rest : { ...rest, trashedAt: at };
}

/** L'entree passe-t-elle le filtre de corbeille de la vue ? */
export function matchesTrashFilter(secrets: EntrySecrets, filter: TrashFilter): boolean {
  if (filter === 'include') return true;
  return isTrashed(secrets) === (filter === 'only');
}

/** Le delai de conservation est-il ecoule a `now` ? */
export function isTrashExpired(secrets: EntrySecrets, now: number): boolean {
  const at = trashedAt(secrets);
  return at !== null && now - at >= TRASH_RETENTION_DAYS * DAY_MS;
}

/** Jours restants avant la purge (au moins 1 tant que l'entree n'est pas purgee), `null` hors corbeille. */
export function trashDaysLeft(secrets: EntrySecrets, now: number): number | null {
  const at = trashedAt(secrets);
  if (at === null) return null;
  return Math.max(1, Math.ceil((at + TRASH_RETENTION_DAYS * DAY_MS - now) / DAY_MS));
}
